import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { ChapterHeading } from "@/components/Reveal";

const FAQS = [
  {
    q: "A CliniAtiva integra com a minha agenda atual?",
    a: "Sim. Na etapa de diagnóstico mapeamos as ferramentas que você já usa — sistema de gestão, agenda online ou até planilha — e configuramos o agente para trabalhar com elas. Se algo não puder ser integrado diretamente, mostramos a alternativa antes da ativação. Você não precisa trocar de sistema.",
  },
  {
    q: "Em quanto tempo começa a funcionar?",
    a: "A ativação leva poucos dias e passa por quatro etapas: diagnóstico, configuração, testes guiados e entrada em operação. O prazo exato depende da complexidade dos seus fluxos de atendimento — e você acompanha e aprova cada etapa antes de o agente entrar no ar.",
  },
  {
    q: "Preciso trocar meu número de WhatsApp?",
    a: "Não. A CliniAtiva opera no número que seus clientes já conhecem. Nada muda para quem entra em contato — apenas a velocidade e a disponibilidade das respostas.",
  },
  {
    q: "Meus clientes vão saber que estão falando com uma IA?",
    a: "As conversas são naturais e usam o tom de voz do seu negócio. Você decide o nível de transparência: o atendimento pode se identificar como assistente virtual sempre que você preferir. Nossa recomendação é ser transparente — isso aumenta a confiança do cliente.",
  },
  {
    q: "E se a IA não souber responder?",
    a: "Você define as regras de encaminhamento. Quando uma conversa sai do fluxo combinado, envolve um caso sensível ou o cliente pede uma pessoa, o atendimento é transferido para a sua equipe — com o histórico completo da conversa, sem o cliente precisar repetir nada.",
  },
  {
    q: "Quais são os limites da solução?",
    a: "A CliniAtiva automatiza atendimento e agendamento — e só isso. Ela não substitui julgamento profissional, não dá orientações clínicas ou técnicas e não executa tarefas fora dos fluxos configurados. O que exige decisão humana vai para humanos.",
  },
  {
    q: "E se eu quiser cancelar?",
    a: "No plano mensal não há fidelidade: você cancela quando quiser e o serviço permanece ativo até o fim do ciclo já pago. No plano anual, o compromisso é do período contratado, sem reembolso proporcional. Seus dados e configurações são seus.",
  },
  {
    q: "Como meus dados e os dos meus clientes são tratados?",
    a: "Usamos os dados apenas para operar o atendimento do seu negócio, em conformidade com a LGPD. Não vendemos dados e você pode solicitar correção ou exclusão a qualquer momento. Os detalhes estão na nossa Política de Privacidade, disponível no rodapé e no formulário de demonstração.",
  },
];

const Item = ({ faq, i, open, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.55, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
    className={`overflow-hidden rounded-2xl border transition-colors duration-300 ${
      open ? "border-[var(--brand)]/50 bg-[var(--card)]" : "border-[var(--line)] bg-transparent hover:border-[var(--brand)]/30"
    }`}
  >
    <button
      data-testid={`faq-item-${i + 1}`}
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left sm:px-8"
    >
      <span className="font-display text-base font-semibold text-[var(--ink)] sm:text-lg">{faq.q}</span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.3 }}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
          open ? "border-[var(--brand)] bg-[var(--brand)] text-petrol" : "border-[var(--line)] text-[var(--muted)]"
        }`}
      >
        <Plus className="h-4 w-4" />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="px-6 pb-6 text-sm leading-relaxed text-[var(--muted)] sm:px-8 sm:text-base">{faq.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

export default function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-4xl px-5 lg:px-10">
        <ChapterHeading
          index="11"
          eyebrow="Perguntas frequentes"
          align="center"
          lines={["Antes que você", "pergunte."]}
        />
        <div className="mt-14 space-y-3">
          {FAQS.map((f, i) => (
            <Item key={f.q} faq={f} i={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}
